import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { KnowledgeArticle } from '../../models/learn/knowledge-article';

/**
 * Builds the #51 SEO surface for the redesigned knowledge centre:
 *  - JSON-LD objects (`Article`/`TechArticle` + `BreadcrumbList` for the reader; `Organization`
 *    and `WebSite`+`SearchAction` for the hub/site). NO `FAQPage`/`HowTo` — Google retired those
 *    rich results May 2026, so FAQ answers are modelled as `Article` (a passive semantic signal only).
 *  - A self-referencing `<link rel="canonical">` per page, and `<link rel="alternate" hreflang>`
 *    scaffolding (the article `language`, plus `x-default`) so the locale model is hreflang-ready
 *    even while only `en` ships today.
 *
 * The JSON-LD is emitted into the document head by the {@link JsonLdComponent} in each page template,
 * so it is present in the server-rendered HTML (view-source), not JS-injected. The canonical/hreflang
 * links are written straight onto `<head>` via the injected {@link DOCUMENT}, which is the SSR DOM
 * under Angular Universal — so they too land in server HTML. All DOM work is null-guarded for safety.
 */
@Injectable({ providedIn: 'root' })
export class KnowledgeSeoService {
  /** schema.org JSON-LD context, shared by every graph below. */
  private static readonly SCHEMA_CONTEXT = 'https://schema.org';

  /** Publisher identity reused across every JSON-LD graph. */
  private static readonly ORGANIZATION = {
    '@type': 'Organization',
    name: 'pitchIN',
    url: environment.baseUrl,
    logo: `${environment.baseUrl}/assets/images/logo/pitchin-logo.png`,
  };

  constructor(@Inject(DOCUMENT) private readonly doc: Document) {}

  /** Absolute portal URL for an article, matching the API sitemap `<loc>` exactly (self-canonical). */
  articleUrl(article: Pick<KnowledgeArticle, 'slug'>, collection: 'learn' | 'faq'): string {
    const base = this.base();
    return collection === 'faq' ? `${base}/learn/faq/${article.slug}` : `${base}/learn/read/${article.slug}`;
  }

  /**
   * `Article` (FAQ answers use `TechArticle` — never `FAQPage`) JSON-LD with headline, dates, author,
   * and the {@link KnowledgeSeoService.ORGANIZATION} publisher. `mainEntityOfPage` self-references the
   * canonical so the markup and the canonical agree.
   */
  buildArticleLd(article: KnowledgeArticle, collection: 'learn' | 'faq'): { [key: string]: any } {
    const url = this.articleUrl(article, collection);
    const ld: { [key: string]: any } = {
      '@context': KnowledgeSeoService.SCHEMA_CONTEXT,
      '@type': collection === 'faq' ? 'TechArticle' : 'Article',
      headline: article.title,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      url,
      inLanguage: article.language || 'en',
      author: { '@type': 'Organization', name: article.author || 'pitchIN Team' },
      publisher: KnowledgeSeoService.ORGANIZATION,
    };
    if (article.updatedAt) {
      // dateModified does quadruple duty (sitemap + schema + GEO + visible date). Use it for
      // datePublished too when no distinct publish date is carried, rather than omitting dates.
      ld['dateModified'] = article.updatedAt;
      ld['datePublished'] = article.updatedAt;
    }
    return ld;
  }

  /**
   * `BreadcrumbList` JSON-LD from the reader's breadcrumb trail. The leaf (the article itself)
   * points at the canonical; the intermediate crumbs point at their lane landing pages.
   */
  buildBreadcrumbLd(trail: string[], article: KnowledgeArticle, collection: 'learn' | 'faq'): { [key: string]: any } {
    const base = this.base();
    const laneUrl = collection === 'faq' ? `${base}/learn/faq` : `${base}/learn/read`;
    const urls = [`${base}/learn/hub`, laneUrl, this.articleUrl(article, collection)];
    return {
      '@context': KnowledgeSeoService.SCHEMA_CONTEXT,
      '@type': 'BreadcrumbList',
      itemListElement: trail.map((name, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name,
        item: urls[i] ?? urls[urls.length - 1],
      })),
    };
  }

  /**
   * `Organization` + `WebSite`(with a `SearchAction` pointing at the knowledge search) JSON-LD for the
   * hub / site. The `SearchAction` target is the hub with a `?q={search_term_string}` template so search
   * engines can surface a sitelinks searchbox.
   */
  buildSiteLd(): { [key: string]: any } {
    const base = this.base();
    return {
      '@context': KnowledgeSeoService.SCHEMA_CONTEXT,
      '@graph': [
        KnowledgeSeoService.ORGANIZATION,
        {
          '@type': 'WebSite',
          name: 'pitchIN Knowledge Centre',
          url: `${base}/learn/hub`,
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${base}/learn/hub?q={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        },
      ],
    };
  }

  /**
   * Writes the self-referencing `<link rel="canonical">` and the `<link rel="alternate" hreflang>`
   * scaffolding (the page language + an `x-default`) onto `<head>`. Idempotent — removes any prior
   * tags this service added before writing, so navigating between articles never stacks duplicates.
   * SSR-safe: runs against the injected {@link DOCUMENT}, which is the server DOM under Universal.
   */
  setCanonicalAndHreflang(url: string, language: string | undefined): void {
    const head = this.doc?.head;
    if (!head) {
      return;
    }
    this.clearManagedLinks();

    const canonical = this.doc.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', url);
    canonical.setAttribute('data-kc-seo', 'canonical');
    head.appendChild(canonical);

    // hreflang scaffolding: the page's own locale + x-default both self-reference today (en-only),
    // so the reciprocal-return-tag rule holds the moment a second locale is added.
    const lang = (language || 'en').toLowerCase();
    for (const hreflang of [lang, 'x-default']) {
      const alt = this.doc.createElement('link');
      alt.setAttribute('rel', 'alternate');
      alt.setAttribute('hreflang', hreflang);
      alt.setAttribute('href', url);
      alt.setAttribute('data-kc-seo', 'hreflang');
      head.appendChild(alt);
    }
  }

  /** Removes the canonical + hreflang tags this service previously added (idempotent re-render). */
  clearManagedLinks(): void {
    this.removeFromHead('link[data-kc-seo]');
  }

  /**
   * Writes a JSON-LD `<script>` into `<head>` for pages that don't render the {@link JsonLdComponent}
   * in their template (e.g. the standalone hub). SSR-safe via the injected DOCUMENT; idempotent —
   * replaces any prior script this service added under the same `slot`. The `</script>` token is
   * escaped so the payload can't break out of the script element.
   */
  setHeadJsonLd(ld: { [key: string]: any }, slot: string): void {
    const head = this.doc?.head;
    if (!head) {
      return;
    }
    this.removeFromHead(`script[data-kc-seo-ld="${slot}"]`);

    const script = this.doc.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-kc-seo-ld', slot);
    script.textContent = JSON.stringify(ld).replace(/<\/script>/gi, '<\\/script>');
    head.appendChild(script);
  }

  /** Removes a head JSON-LD script previously added under `slot`. */
  clearHeadJsonLd(slot: string): void {
    this.removeFromHead(`script[data-kc-seo-ld="${slot}"]`);
  }

  /** Removes every managed `<head>` element matching `selector`. SSR-safe; no-op without a head. */
  private removeFromHead(selector: string): void {
    const head = this.doc?.head;
    if (!head) {
      return;
    }
    head.querySelectorAll(selector).forEach((el) => el.parentNode?.removeChild(el));
  }

  private base(): string {
    return (environment.baseUrl || '').replace(/\/+$/, '');
  }
}
